// ============================================================================
// Upgrade Page — plan comparison + Stripe payment link
// ============================================================================

import { useState, useEffect, useCallback } from "react"
import { useSearchParams } from "react-router-dom"
import { PLANS, FREE_FILL_LIMIT, type PlanId } from "../../core/payments/plans"
import { getUsageCount, getCurrentPlan } from "../../core/payments/usage"
import { CheckCircle } from "lucide-react"

export function UpgradePage() {
  const [usageCount, setUsageCount] = useState(0)
  const [currentPlan, setCurrentPlan] = useState<PlanId>("free")
  const [message, setMessage] = useState<{
    type: "success" | "error" | "info"
    text: string
  } | null>(null)
  const [searchParams] = useSearchParams()

  useEffect(() => {
    getUsageCount().then(setUsageCount)
    getCurrentPlan().then(setCurrentPlan)
  }, [])

  useEffect(() => {
    const checkoutResult = searchParams.get("checkout")
    if (checkoutResult === "success") {
      setMessage({
        type: "success",
        text: "Payment successful! Your Pro subscription is now active.",
      })
      window.history.replaceState({}, "", "/app/upgrade")
      getCurrentPlan().then(setCurrentPlan)
    } else if (checkoutResult === "cancel") {
      setMessage({
        type: "info",
        text: "Checkout was cancelled. No charges were made.",
      })
      window.history.replaceState({}, "", "/app/upgrade")
    }
  })

  const handleUpgrade = useCallback(() => {
    window.open("https://buy.stripe.com/4gM00j9Nr3lBh2G67u4ko00", "_blank")
  }, [])

  return (
    <div className="max-w-3xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">Upgrade Your Plan</h1>

      {message && (
        <div
          className={`border rounded-lg p-4 mb-6 ${
            message.type === "success"
              ? "bg-green-50 border-green-300 text-green-800"
              : message.type === "error"
                ? "bg-red-50 border-red-300 text-red-800"
                : "bg-blue-50 border-blue-300 text-blue-800"
          }`}
        >
          <p className="text-sm font-medium">{message.text}</p>
        </div>
      )}

      {currentPlan === "free" && !message && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-6">
          <strong>
            {usageCount}/{FREE_FILL_LIMIT} free fills used this month
          </strong>
          <p className="text-sm text-amber-700 mt-1">
            Resets on the 1st of each month
          </p>
        </div>
      )}

      {currentPlan === "pro" && !message && (
        <div className="bg-green-50 border border-green-300 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <strong>You're on the Pro plan</strong>
          </div>
          <p className="text-sm text-green-700 mt-1">
            Unlimited autofills, all features unlocked
          </p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {Object.values(PLANS).map((plan) => (
          <div
            key={plan.id}
            className={`border-2 rounded-xl p-6 ${
              currentPlan === plan.id
                ? "border-green-500 bg-green-50"
                : "border-gray-200"
            }`}
          >
            <h2 className="text-xl font-bold">{plan.name}</h2>
            <p className="text-3xl font-bold my-3">
              {plan.price === 0 ? "Free" : `$${plan.price}/mo`}
            </p>
            <ul className="space-y-2 mb-6">
              {plan.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <span className="text-green-600 mt-0.5">&#10003;</span>
                  {f}
                </li>
              ))}
            </ul>
            {plan.id === "pro" && currentPlan === "free" && (
              <button
                onClick={handleUpgrade}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition"
              >
                Upgrade to Pro
              </button>
            )}
            {plan.id === "free" && currentPlan === "free" && (
              <div className="text-center text-sm text-gray-500 py-3">
                Current plan
              </div>
            )}
            {currentPlan === "pro" && plan.id === "pro" && (
              <div className="text-center text-sm text-green-600 py-1 font-medium">
                Active
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
